"use client"

import { useEffect, useRef } from 'react'
import resolveCloudflareSrcSet from '@components/image/functions/resolveCloudflareSrcSet'
import cloudflarePhoto from '@utils/links/cloudflarePhoto'

import styles from './OfferCard.module.css'

const SRC_SET = { '300w': 'list', '568w': 'x0grid', '800w': 'card43m' }
const GAL = `.${styles.gal}`

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const indexOf = (track) => Math.round(track.scrollLeft / (track.clientWidth || 1))

// Kolejne zdjęcia (data-gal; pierwsze jest zdjęciem karty) wstawiane do slajdów 1…upto — ten sam element, srcset
// i sizes co przy wyświetleniu, więc bez ponownego pobrania. low: z niskim priorytetem (zapas przed przesunięciem).
function ensure (card, upto, low = false) {
    const track = card?.querySelector(GAL)
    if (!track) return
    const ids = (track.dataset.gal || '').split(',').filter(Boolean)
    const sizes = card.querySelector(`.${styles.photo}`)?.sizes || '100vw'
    for (let i = 0; i < Math.min(upto, ids.length); i++) {
        const slide = track.children[i + 1]
        if (!slide || slide.querySelector('img')) continue
        const img = document.createElement('img')
        img.alt = ''
        img.decoding = 'async'
        if (low) img.setAttribute('fetchpriority', 'low')
        img.sizes = sizes
        img.srcset = resolveCloudflareSrcSet(SRC_SET, ids[i])
        img.src = cloudflarePhoto(ids[i], 'x0grid')
        img.onerror = () => { img.hidden = true }
        slide.appendChild(img)
    }
}
// przy zdjęciu N gotowe także N+1
const ahead = (card) => { const track = card?.querySelector(GAL); if (track) ensure(card, indexOf(track) + 1) }

function sync (card, index) {
    const track = card.querySelector(GAL)
    const n = track.children.length
    card.querySelector(`.${styles.galDots}`)?.style.setProperty('--i', index)
    const [prev, next] = card.querySelectorAll('[data-gal-step]')
    if (prev) prev.disabled = index <= 0
    if (next) next.disabled = index >= n - 1
}


// Jeden kontroler dla wszystkich kart listy (bez osobnej wyspy w każdej karcie).
export default function GalleryController () {
    const live = useRef(null)

    useEffect(() => {
        const frames = new WeakMap()
        const cardOf = (el) => el?.closest?.('article')

        const onOver = (e) => {
            const card = cardOf(e.target)
            if (card?.querySelector(GAL)) ahead(card)
        }
        const onFocus = (e) => { if (e.target.matches?.('[data-gal-step]')) ahead(cardOf(e.target)) }
        const onTouch = (e) => { if (e.target.closest?.(GAL)) ahead(cardOf(e.target)) }
        // przesunięcie palcem: kropka i zapas zdjęć na bieżąco (raz na klatkę), bez czekania na koniec przewijania
        const onScroll = (e) => {
            const track = e.target
            if (!(track instanceof Element) || !track.matches(GAL) || frames.get(track)) return
            frames.set(track, requestAnimationFrame(() => {
                frames.delete(track)
                const card = cardOf(track), index = indexOf(track)
                sync(card, index)
                ensure(card, index + 1)
            }))
        }
        // telefon (bez najechania kursorem): drugie zdjęcie z niskim priorytetem, gdy karta wchodzi na ekran
        let io = null, mo = null
        if (window.matchMedia('(hover: none)').matches && 'IntersectionObserver' in window) {
            io = new IntersectionObserver((entries) => entries.forEach((en) => {
                if (!en.isIntersecting) return
                io.unobserve(en.target)
                ensure(cardOf(en.target), 1, true)
            }))
            const watch = () => document.querySelectorAll(`${GAL}:not([data-io])`).forEach((t) => { t.dataset.io = ''; io.observe(t) })
            watch()
            // nowe karty po zmianie filtrów i stronicowaniu
            let pending = false
            mo = new MutationObserver(() => { if (pending) return; pending = true; requestAnimationFrame(() => { pending = false; watch() }) })
            mo.observe(document.body, { childList: true, subtree: true })
        }
        const onClick = (e) => {
            const step = e.target.closest?.('[data-gal-step]')
            const card = cardOf(e.target)
            if (step) {
                const track = card.querySelector(GAL)
                const n = track.children.length
                const index = Math.max(0, Math.min(n - 1, indexOf(track) + Number(step.dataset.galStep)))
                ensure(card, index + 1)
                track.scrollTo({ left: index * track.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' })
                sync(card, index)
                if (live.current) live.current.textContent = `Zdjęcie ${index + 1} z ${n}`
                // przycisk na końcu toru wyłączony: fokus na drugi
                if (step.disabled) card.querySelector(`[data-gal-step="${-Number(step.dataset.galStep)}"]`)?.focus()
                return
            }
            // dotknięcie zdjęcia otwiera ogłoszenie (ten sam link co tytuł)
            if (e.target.closest?.(GAL)) card?.querySelector(`.${styles.title} a`)?.click()
        }

        document.addEventListener('pointerover', onOver, { passive: true })
        document.addEventListener('focusin', onFocus)
        document.addEventListener('touchstart', onTouch, { passive: true, capture: true })
        document.addEventListener('scroll', onScroll, { passive: true, capture: true })
        document.addEventListener('click', onClick)
        return () => {
            io?.disconnect(); mo?.disconnect()
            document.removeEventListener('pointerover', onOver)
            document.removeEventListener('focusin', onFocus)
            document.removeEventListener('touchstart', onTouch, { capture: true })
            document.removeEventListener('scroll', onScroll, { capture: true })
            document.removeEventListener('click', onClick)
        }
    }, [])

    return <p aria-live="polite" className={styles.srOnly} ref={live} role="status" />
}
